import { useMutation, useQuery } from "@apollo/client/react";
import { ArrowRightOutlined, HomeOutlined, LogoutOutlined, MailOutlined, TeamOutlined } from "@ant-design/icons";
import { css } from "@linaria/core";
import { Alert, Button, Card, Flex, Form, Input, Result, Spin, Typography } from "antd";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import LogoSvg from "../../assets/logo.svg?react";
import { LanguageSwitcher } from "../../components/LanguageSwitcher.tsx";
import { LoadingBlocker } from "../../components/LoadingBlocker.tsx";
import {
  CompleteEmailLinkDocument,
  EmailLinkMode,
  GetCurrentLoggedUserDocument,
  GetEmailLinkPreviewDocument,
  LogoutDocument,
} from "../../gql/generated/graphqlSchema.tsx";
import { useSafeSearchParams } from "../../hooks/useSafeSearchParams.ts";
import { useServiceError, useServiceErrorDetails } from "../../hooks/useServiceError.ts";
import { routes } from "../../routes.ts";

const { Title, Text } = Typography;

const page = css`
  min-height: 100vh;
  background: var(--ant-color-bg-layout);
`;

const logo = css`
  display: block;
  width: 64px;
  height: 64px;
  margin: 0 auto var(--ant-margin-sm);
  border-radius: var(--ant-border-radius-lg);
`;

const productName = css`
  text-align: center;
  margin-top: 0;
  margin-bottom: var(--ant-margin-lg);
`;

const card = css`
  width: 100%;
  max-width: 460px;
  box-shadow: var(--ant-box-shadow-secondary);
`;

const langSwitcher = css`
  margin-top: var(--ant-margin-sm);
`;

const titleStyle = css`
  text-align: center;
  margin-top: 0;
  margin-bottom: var(--ant-margin-xxs);
`;

const subtitle = css`
  display: block;
  text-align: center;
  margin-bottom: var(--ant-margin-md);
`;

const inviteBanner = css`
  margin-bottom: var(--ant-margin);
`;

const alert = css`
  margin-bottom: var(--ant-margin);
`;

const headerBlock = (
  <>
    <LogoSvg className={logo} />
    <Title className={productName} level={3}>
      CALCULABEL
    </Title>
  </>
);

// Strip Result's default 48px/32px outer padding so the content sits inside
// the Card's natural body padding — same spacing as LoginPage which renders
// the Card content directly without a Result wrapper.
const resultStyles = { root: { padding: 0 }, body: { padding: 0 } };

const searchParamsSchema = z.object({
  token: z.uuid().optional().catch(undefined),
});

type FormValues = { name: string };

export const VerifyEmailLinkPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useSafeSearchParams(searchParamsSchema);
  const { data: currentUserData, loading: loadingCurrentUser } = useQuery(GetCurrentLoggedUserDocument);
  const { data: previewData, loading: previewLoading } = useQuery(GetEmailLinkPreviewDocument, {
    variables: { token: token ?? "" },
    skip: token == null,
  });
  const [logout, { loading: loggingOut }] = useMutation(LogoutDocument, {
    refetchQueries: [GetCurrentLoggedUserDocument],
    awaitRefetchQueries: true,
  });
  const [completeEmailLink, { loading: completing, error: completeError }] = useMutation(CompleteEmailLinkDocument, {
    refetchQueries: [GetCurrentLoggedUserDocument],
    awaitRefetchQueries: true,
  });

  const preview = previewData?.emailLinkPreview;
  const previewLocale = preview?.locale ?? null;
  useEffect(() => {
    if (previewLocale != null && i18n.language !== previewLocale) {
      void i18n.changeLanguage(previewLocale);
    }
  }, [previewLocale, i18n]);

  const errorMessage = useServiceError(completeError);
  const errorDetails = useServiceErrorDetails(completeError);
  const accountAlreadyExists = errorDetails?.code === "error.team.cannot-join-account-exists";

  const autoCompletedRef = useRef(false);
  useEffect(() => {
    if (
      preview != null &&
      preview.mode === EmailLinkMode.Login &&
      currentUserData?.getCurrentLoggedUser == null &&
      token != null &&
      !autoCompletedRef.current
    ) {
      autoCompletedRef.current = true;
      completeEmailLink({ variables: { input: { token } } })
        .then((result) => {
          if (result.data?.completeEmailLink) {
            void navigate(routes.recipes.to(), { replace: true });
          }
          // Service errors (expired/used token, etc.) land in `completeError` and
          // are surfaced below — nothing more to do here.
        })
        .catch(() => {
          // Network failure — keep the user on the page; the error banner picks it up.
        });
    }
  }, [preview, currentUserData, token, completeEmailLink, navigate]);

  const onSubmitName = async (values: FormValues) => {
    if (token == null) return;
    try {
      const result = await completeEmailLink({
        variables: { input: { token, name: values.name.trim() } },
      });
      if (result.data?.completeEmailLink) {
        void navigate(routes.recipes.to(), { replace: true });
      }
    } catch {
      // Network failure — Apollo populates `completeError`, the inline Alert renders it.
    }
  };

  if (loadingCurrentUser || previewLoading) {
    return <Spin fullscreen size="large" />;
  }

  const currentUser = currentUserData?.getCurrentLoggedUser;
  const invalidLink = token == null || preview == null;

  return (
    <Flex align="center" className={page} justify="center" vertical>
      <LoadingBlocker loading={completing} />
      <Card className={card}>
        {invalidLink ? (
          <Result
            extra={
              <Button
                icon={<MailOutlined />}
                onClick={() => void navigate(routes.login.to())}
                size="large"
                type="primary"
              >
                {t("auth.requestNewLink")}
              </Button>
            }
            icon={headerBlock}
            status="warning"
            styles={resultStyles}
            subTitle={t("auth.linkInvalidSubtitle")}
            title={t("auth.linkInvalidTitle")}
          />
        ) : currentUser != null ? (
          <Result
            extra={
              <Button
                icon={<LogoutOutlined />}
                loading={loggingOut}
                onClick={() => void logout()}
                size="large"
                type="primary"
              >
                {t("layout.signOut")}
              </Button>
            }
            icon={headerBlock}
            status="info"
            styles={resultStyles}
            subTitle={
              <Trans
                components={{
                  currentMailto: <a href={`mailto:${currentUser.email}`} />,
                  newMailto: <a href={`mailto:${preview.email}`} />,
                }}
                i18nKey="auth.alreadySignedIn"
                values={{
                  currentName: currentUser.name,
                  currentEmail: currentUser.email,
                  newEmail: preview.email,
                }}
              />
            }
            title={t("auth.alreadySignedInTitle")}
          />
        ) : preview.mode === EmailLinkMode.Login ? (
          accountAlreadyExists && preview.invitation?.inviterName && preview.invitation.inviterEmail ? (
            <Result
              extra={
                <Button
                  icon={<HomeOutlined />}
                  onClick={() => void navigate(routes.landing.to())}
                  size="large"
                  type="primary"
                >
                  {t("auth.backToHome")}
                </Button>
              }
              icon={headerBlock}
              status="warning"
              styles={resultStyles}
              subTitle={
                <Trans
                  components={{
                    inviteeMailto: <a href={`mailto:${preview.email}`} />,
                    inviterMailto: <a href={`mailto:${preview.invitation.inviterEmail}`} />,
                  }}
                  i18nKey="auth.cannotJoinAccountExists"
                  values={{
                    inviteeEmail: preview.email,
                    inviterName: preview.invitation.inviterName,
                    inviterEmail: preview.invitation.inviterEmail,
                  }}
                />
              }
              title={t("auth.cannotJoinTeamTitle")}
            />
          ) : completeError ? (
            <Result
              extra={
                <Button
                  icon={<MailOutlined />}
                  onClick={() => void navigate(routes.login.to())}
                  size="large"
                  type="primary"
                >
                  {t("auth.requestNewLink")}
                </Button>
              }
              icon={headerBlock}
              status="warning"
              styles={resultStyles}
              subTitle={errorMessage ?? t("auth.linkInvalidSubtitle")}
              title={t("auth.linkInvalidTitle")}
            />
          ) : (
            <Result
              icon={headerBlock}
              status="info"
              styles={resultStyles}
              subTitle={t("auth.signingIn")}
              title={preview.email}
            />
          )
        ) : (
          <>
            {headerBlock}
            <Title className={titleStyle} level={3}>
              {t("auth.completeSignup")}
            </Title>
            <Text className={subtitle} type="secondary">
              {t("auth.completeSignupSubtitle")}
            </Text>

            {preview.invitation ? (
              <Alert
                className={inviteBanner}
                icon={<TeamOutlined />}
                showIcon
                title={
                  preview.invitation.inviterName && preview.invitation.inviterEmail
                    ? t("auth.invitedByEmail", {
                        name: preview.invitation.inviterName,
                        email: preview.invitation.inviterEmail,
                      })
                    : preview.invitation.inviterName
                      ? t("auth.invitedBy", { name: preview.invitation.inviterName })
                      : null
                }
                type="info"
              />
            ) : null}

            <Form<FormValues> layout="vertical" onFinish={onSubmitName} requiredMark={false}>
              <Form.Item
                label={t("common.name")}
                name="name"
                rules={[{ required: true, message: t("error.auth.signup-name-required") }]}
              >
                <Input autoComplete="name" placeholder={t("auth.namePlaceholder")} size="large" />
              </Form.Item>

              <Form.Item label={t("common.email")}>
                <Input disabled size="large" value={preview.email} />
              </Form.Item>

              {errorMessage ? <Alert className={alert} showIcon title={errorMessage} type="error" /> : null}

              <Form.Item>
                <Button
                  block
                  htmlType="submit"
                  icon={<ArrowRightOutlined />}
                  iconPlacement="end"
                  loading={completing}
                  size="large"
                  type="primary"
                >
                  {t("auth.continueButton")}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Card>

      <div className={langSwitcher}>
        <LanguageSwitcher />
      </div>
    </Flex>
  );
};
